/**
 * MED-1346: asks the coach whether a late change came with less than 24
 * hours' notice, then posts the answer. Used after a coach's own date edit or
 * cancel, and from the dashboard card. Closing the window leaves the claim
 * pending, so the dashboard card asks again.
 */

import { Button, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { missClaimsApi } from '../../services/api/missClaims';
import { getApiErrorMessage } from '../../services/api/client';
import { getDisplayTimezone } from '../../utils/timezone';
import { getZoneCode } from '../../utils/formatters';
import type { MissClaim } from '../../types';
import { missAnswerText, missQuestionText } from './missClaimText';

/** "1:1 on Tue, Oct 7, 2:00 PM ET", in the viewer's display timezone. */
export function missSessionLabel(originalStart: string): string {
  const date = new Date(originalStart);
  const timeZone = getDisplayTimezone();
  const text = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(date);
  return `1:1 on ${text} ${getZoneCode(timeZone, date)}`;
}

/**
 * F11: the result of a no-show or late change answer stays on screen until the
 * user closes it, so a reschedule ask is not lost in a toast.
 */
export function showMissResult(title: string, message: string) {
  const modalId = `miss-result-${Date.now()}`;
  modals.open({
    modalId,
    title,
    // Centered so the top-right toasts never cover the close button.
    centered: true,
    children: (
      <Stack gap="md">
        <Text size="sm">{message}</Text>
        <Group justify="flex-end">
          <Button color="medleyGreen" onClick={() => modals.close(modalId)}>
            OK
          </Button>
        </Group>
      </Stack>
    ),
  });
}

export function openMissClaimQuestion(
  claim: MissClaim,
  onAnswered?: (claim: MissClaim) => void,
  asStaff = false
) {
  const answer = async (underTwentyFourHours: boolean) => {
    try {
      const { claim: answered } = await missClaimsApi.answer(
        claim.cohortId,
        claim.sessionId,
        claim.id,
        underTwentyFourHours
      );
      // A reschedule with 24 hours' notice or more gets no message (ticket).
      const message = missAnswerText(answered);
      if (message) showMissResult('Answer saved', message);
      onAnswered?.(answered);
    } catch (error) {
      notifications.show({
        title: 'Could not save your answer',
        message: getApiErrorMessage(
          error,
          'Please try again from your dashboard.'
        ),
        color: 'red',
      });
      // A 409 means someone answered first, so the caller reloads either way.
      onAnswered?.(claim);
    }
  };

  modals.openConfirmModal({
    title: 'Late change notice',
    centered: true,
    // A toast sits on the backdrop, so a click on its close was an outside click
    // that closed this question unanswered. Close only with X, Esc or an answer.
    closeOnClickOutside: false,
    children: (
      <Text size="sm">
        {missQuestionText(claim, missSessionLabel(claim.originalStart), asStaff)}
      </Text>
    ),
    labels: {
      confirm: "Yes, less than 24 hours' notice",
      cancel: '24 hours or more',
    },
    confirmProps: { color: 'medleyGreen' },
    cancelProps: { variant: 'default' },
    onConfirm: () => answer(true),
    onCancel: () => answer(false),
  });
}
